import React from 'react';
import clsx from 'clsx';

import { Button } from '@edx/components/button';
import { ResponsiveImage } from '@edx/graphql/generated';
import { Body, Title, Intro } from '@edx/components/typography';

type HeroButton = { text: string; link?: string; onClick?: () => void };

type Props = {
  bgImage: ResponsiveImage;
  preheading?: string;
  heading: string;
  subheading?: string;
  primaryButton?: HeroButton;
  secondaryButton?: HeroButton;
  noBorder?: boolean;
  variant?: 'small' | 'medium' | 'large';
  sectionClassName?: string;
  containerClassName?: string;
  contentClassName?: string;
};

export const Hero = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      bgImage,
      preheading,
      heading,
      subheading,
      primaryButton,
      secondaryButton,
      noBorder,
      variant = 'medium',
      sectionClassName,
      containerClassName,
      contentClassName,
    },
    ref
  ) => (
    <section
      className={clsx('relative overflow-hidden', sectionClassName, {
        'border-b border-gray-700': !noBorder,
      })}
      ref={ref}
    >
      <div
        className={clsx('absolute inset-0 overflow-hidden', {
          'min-h-30vh max-h-50vh': variant === 'small',
          'min-h-40vh max-h-70vh': variant === 'medium',
          'min-h-50vh max-h-80vh': variant === 'large',
        })}
      >
        <picture>
          <source media="(min-width: 1122px)" srcSet={bgImage.large} />
          <source media="(min-width: 641px)" srcSet={bgImage.medium} />
          <img className="h-full w-full object-cover object-bottom" src={bgImage.small} alt="" />
        </picture>
      </div>

      <div
        className={clsx('relative flex items-center justify-center', containerClassName, {
          'py-56 min-h-50vh': variant === 'small',
          'py-56 min-h-70vh': variant === 'medium',
          'py-24 min-h-80vh': variant === 'large',
        })}
      >
        <div
          className={clsx(
            'flex flex-col items-center max-w-6xl px-4 mx-auto text-center text-gray-500',
            contentClassName
          )}
        >
          {preheading && (
            <Intro
              tag="h4"
              size="small"
              className={clsx('text-gray-500 md:text-intro', {
                'mb-2': variant !== 'medium',
              })}
            >
              {preheading}
            </Intro>
          )}

          <Title
            tag="h1"
            className="max-w-2xl leading-tight md:leading-none text-gray-300 md:text-large lg:text-xlarge"
          >
            {heading}
          </Title>

          {subheading && (
            <Body tag="h3" size="large" className="max-w-xl text-center text-gray-300 mt-3">
              {subheading}
            </Body>
          )}

          {primaryButton && (
            <div className="justify-center">
              <Button
                {...(primaryButton?.link ? { href: primaryButton.link } : { onClick: primaryButton.onClick })}
                size={variant === 'large' ? 'xlarge' : 'large'}
                textVariant="medium"
                className="mt-8"
              >
                {primaryButton.text}
              </Button>

              {secondaryButton && (
                <Button
                  href={secondaryButton.link}
                  variant="light"
                  size="large"
                  textVariant="medium"
                  className="mt-8 ml-2"
                >
                  {secondaryButton.text}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
);
